import MetaCRMTag from "@metacrm/metacrm-material-ui/dist/MetaCRMTag";
import { Box } from "@mui/material";
import { formatDate, formatWalletAgeDisplay } from "features/helpers/utils";
import { useFetchKolList } from "features/metadesk/redux/fetchKolList";
import useNavigateEntity from "hooks/useNavigateEntity";
import { cloneDeep, isObject, omit } from "lodash-es";
import { useMemo, useRef, useState } from "react";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import { useOutletContext } from "react-router";
import { useIntl } from "react-intl";

const FilterList = ({ hasFilter = false, openFilterModal }) => {
  const { formatMessage } = useIntl();
  const { kolListFilter: filter, updateKolListFilter } = useFetchKolList();
  const { navigateEntity } = useNavigateEntity();
  const dispatchSnackbar = useDispatchSnackbar();
  const [entityName] = useOutletContext();

  async function handleDeleteFilterKey(key, value) {
    let newFilter = cloneDeep(filter);
    // Handle the case when the key is an array of objects, avoiding delete the whole key
    if (value !== undefined) {
      const filteredValues = newFilter[key].filter(
        (key) => key.value !== value
      );
      if (filteredValues.length === 0) {
        newFilter = omit(newFilter, key);
      } else {
        newFilter = { ...newFilter, [key]: filteredValues };
      }
    } else {
      newFilter = omit(filter, key); // Removes the key from the filter
    }
    await updateKolListFilter(newFilter);
  }

  async function handleClearFilter(e) {
    e.stopPropagation();
    updateKolListFilter(
      cloneDeep({
        page: 1,
        orderData: { sort: "xFollowers", order: -1 },
        search: "",
      })
    );
  }

  function validateFilter(value) {
    if (isObject(value) && value?.value === "") return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return value;
  }

  if (!hasFilter) return <></>;
  return (
    <>
      <Box
        width="100%"
        bgcolor="rgba(123, 97, 255, 0.20)"
        p="12px 16px"
        justifyContent="space-between"
        display="flex"
        alignItems="center"
        fontSize={12}
        borderRadius="4px"
        mb="8px"
        sx={{ cursor: "pointer" }}
        onClick={openFilterModal}
      >
        <Box display="flex" alignItems="center">
          <Box
            sx={{ color: "#5643CC" }}
            fontSize={12}
            fontWeight={600}
            mr="20px"
            flexShrink={0}
          >
            {formatMessage({ id: "kol.filters.appliedLabel" })}
          </Box>
          <Box className="item-flex" gap="4px">
            {filter?.xFollowers && (
              <MetaCRMTag
                option={{
                  color: "rgba(255, 255, 255, 0.80)",
                  label: `${formatMessage({
                    id: "kol.filters.followersCount",
                  })} | ${filter?.xFollowers[0]}-${filter?.xFollowers[1]}`,
                }}
                onDelete={() => handleDeleteFilterKey("xFollowers")}
              />
            )}
            {validateFilter(filter?.verified) && (
              <MetaCRMTag
                option={{
                  color: "rgba(255, 255, 255, 0.80)",
                  label: `${formatMessage({
                    id: "kol.filters.verifiedAccount",
                  })}`,
                }}
                onDelete={() => handleDeleteFilterKey("verified")}
              />
            )}
            {validateFilter(filter?.categories) &&
              filter?.categories?.map((item) => (
                <MetaCRMTag
                  option={{
                    color: "rgba(255, 255, 255, 0.80)",
                    label: item.label,
                  }}
                  onDelete={() =>
                    handleDeleteFilterKey("categories", item.value)
                  }
                />
              ))}
            {validateFilter(filter?.language) &&
              filter?.language?.map((item) => (
                <MetaCRMTag
                  option={{
                    color: "rgba(255, 255, 255, 0.80)",
                    label: item.label,
                  }}
                  onDelete={() => handleDeleteFilterKey("language", item.value)}
                />
              ))}
            {validateFilter(filter?.location) &&
              filter?.location?.map((item) => (
                <MetaCRMTag
                  option={{
                    color: "rgba(255, 255, 255, 0.80)",
                    label: item.label,
                  }}
                  onDelete={() => handleDeleteFilterKey("location", item.value)}
                />
              ))}
            {filter?.etr && (
              <MetaCRMTag
                option={{
                  color: "rgba(255, 255, 255, 0.80)",
                  label: `${formatMessage({
                    id: "kol.filters.engagementRate",
                  })} | ${filter?.etr[0]}%-${filter?.etr[1]}%`,
                }}
                onDelete={() => handleDeleteFilterKey("etr")}
              />
            )}
            {filter?.vtr && (
              <MetaCRMTag
                option={{
                  color: "rgba(255, 255, 255, 0.80)",
                  label: `${formatMessage({
                    id: "kol.filters.viewThroughRate",
                  })} | ${filter?.vtr[0]}%-${filter?.vtr[1]}%`,
                }}
                onDelete={() => handleDeleteFilterKey("vtr")}
              />
            )}
            {filter?.originalTweetsRatio && (
              <MetaCRMTag
                option={{
                  color: "rgba(255, 255, 255, 0.80)",
                  label: `${formatMessage({
                    id: "kol.filters.originalTweetsRatio",
                  })} | ${filter?.originalTweetsRatio[0]}%-${
                    filter?.originalTweetsRatio[1]
                  }%`,
                }}
                onDelete={() => handleDeleteFilterKey("originalTweetsRatio")}
              />
            )}
          </Box>
        </Box>

        <Box className="item-flex" flexShrink={0}>
          <Box
            p="4px 8px"
            color="#7B61FF"
            className="item-flex"
            sx={{ cursor: "pointer" }}
            onClick={handleClearFilter}
            fontWeight={700}
          >
            <i className="meta-crm-icon-ic_cancel font-size-12" />
            <Box ml="4px">{formatMessage({ id: "global.filter.clean" })}</Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default FilterList;
